import React, { PropsWithChildren, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { useWidthContext } from '../../providers/Width';
import { LinkWrapper } from '../LinkWrapper/LinkWrapper';
import { Modal } from '../../importCL';

interface IGalleryImage {
    baseUrl?: string;
    rolloverUrl?: string;
    modalUrl?: string;
    link?: string;
    altText?: string;
    maxHeightDesktop?: string;
    maxHeightMobile?: string;
    topMarginMobile?: string;
    bottomMarginMobile?: string;
}

interface IImageGalleryProps extends MappedComponentProperties {
    backgroundColor?: string;
    alignment?: 'left' | 'center';
    rowCount?: string;
    modalViewEnabled?: boolean;
    colorRolloverEnabled?: boolean;
    galleryImages?: IGalleryImage[];
    singleColumnMobile?: boolean;
    topPaddingMobile?: string;
    bottomPaddingMobile?: string;
}

interface IGalleryImageProps {
    image: IGalleryImage;
    rollover: boolean;
    onClick: () => void;
    style: object;
}

export const ImageGalleryEditConfig = {
    emptyLabel: 'ImageGallery',
    isEmpty: (props: PropsWithChildren<IImageGalleryProps>) => {
        return !props || !props.galleryImages || props.galleryImages.length <= 0;
    },
};

const getFlexItemWidth = (columnCount: string | undefined): string => {
    switch (columnCount) {
        case 'one': return '100%';
        case 'two': return '50%';
        case 'three': return '33.3333%';
        case 'four': return '25%';
        case 'five': return '20%';
        case 'six': return '16.6666%';
        default: return '100%';
    }
};

const Image = (props: IGalleryImageProps) => {
    if (props.rollover) {
        const baseUrl = props.image.baseUrl ?? '';
        const rolloverUrl = props.image.rolloverUrl ?? '';
        return (
            <LinkWrapper href={props.image.link}>
                <img
                    loading='lazy'
                    className='w-full h-full mb-5'
                    src={props.image.baseUrl}
                    alt={props.image.altText}
                    onMouseOver={(e) => e.currentTarget.src = rolloverUrl}
                    onMouseLeave={(e) => e.currentTarget.src = baseUrl}
                    onClick={() => props.onClick()}
                    style={props.style}
                />
            </LinkWrapper>
        );
    }

    return (
        <LinkWrapper href={props.image.link}>
            <img
                loading='lazy'
                className='w-full h-full mb-5'
                src={props.image.baseUrl}
                alt={props.image.altText}
                onClick={() => props.onClick()}
                style={props.style}
            />
        </LinkWrapper>
    );
};

const ImageGallery = (props: PropsWithChildren<IImageGalleryProps>): JSX.Element => {
    const { width } = useWidthContext();
    const isMobile = width <= 768;
    const [open, setOpen] = useState(false);
    const [content, setContent] = useState<IGalleryImage | undefined>(undefined);
    const flexItemWidth: string = getFlexItemWidth(isMobile ? props.singleColumnMobile ? 'one' : 'two' : props.rowCount);

    /**
     * Allows the content to be set dynamically allowing a single Modal instance
     *
     * @param {IGalleryImage} image Gallery image to be displayed
     */
    function openModal(image: IGalleryImage) {
        if (!props.modalViewEnabled) {
            return;
        }

        setContent(image);
        setOpen(true);
    }

    return (
        <div id='image-gallery' className='ImageGallery' style={width <= 768 ? { paddingTop: `${props.topPaddingMobile}`, paddingBottom: `${props.bottomPaddingMobile}` } : undefined}>
            <Modal
                modalOpen={setOpen}
                isOpen={open}>
                <img 
                    loading='lazy'
                    className='flex items-center justify-center text-center max-h-[31.25rem]'
                    src={content?.modalUrl}
                    alt={content?.altText}
                />
            </Modal>

            <div
                className={`flex flex-wrap ${width >= 768 ? 'justify-around py-[0.9375rem]' : 'items-center justify-center'}`}
                style={{ backgroundColor: props.backgroundColor }}>
                {props.galleryImages && props.galleryImages.map((image, i) => (
                    <div
                        className={width >= 768 ? 'flex justify-center items-center my-8' : 'w-1/2'}
                        key={i}
                        style={{
                            flexBasis: flexItemWidth,
                            maxWidth: flexItemWidth,
                        }}
                    >
                        <Image
                            image={image}
                            rollover={props.colorRolloverEnabled ?? false}
                            onClick={() => openModal(image)}
                            style={width <= 768 ? { marginTop: image.topMarginMobile, marginBottom: image.bottomMarginMobile, maxHeight: image.maxHeightMobile } : { maxHeight: image.maxHeightDesktop }}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ImageGallery;
